import { API_BASE_URL } from './config';
import { type AdminUser, type Role } from '../hooks/useRBAC';

const USERS_API_URL = `${API_BASE_URL}/users`;

export interface BackendUser {
  id: string;
  name: string;
  email: string;
  role: string;
  avatar: string | null;
  is_active: boolean;
  last_login: string | null;
  created_at: string;
  updated_at: string;
}

// Map Backend User structure to Frontend AdminUser structure
export const adaptUser = (user: BackendUser): AdminUser => ({
  id: user.id,
  name: user.name,
  email: user.email,
  role: user.role as Role,
  avatar: user.avatar || '',
  isActive: user.is_active,
  lastLogin: user.last_login ? new Date(user.last_login).toLocaleString('ar-SA') : '—',
  createdAt: user.created_at ? new Date(user.created_at).toISOString().split('T')[0] : '',
});

// Map Frontend AdminUser structure to Backend request payload
export const toBackendUser = (user: Partial<AdminUser>) => ({
  name: user.name,
  email: user.email,
  role: user.role,
  avatar: user.avatar || null,
  is_active: user.isActive,
});

/**
 * Fetch all users
 */
export async function getUsers(token: string): Promise<AdminUser[]> {
  const response = await fetch(`${USERS_API_URL}/`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => null);
    throw new Error(errorData?.detail || 'فشل جلب قائمة المستخدمين.');
  }

  const data: BackendUser[] = await response.json();
  return data.map(adaptUser);
}

/**
 * Create a new user
 */
export async function createUser(user: Partial<AdminUser>, token: string): Promise<AdminUser> {
  const response = await fetch(`${USERS_API_URL}/`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(toBackendUser(user)),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => null);
    throw new Error(errorData?.detail || 'فشل إنشاء حساب المستخدم الجديد.');
  }

  const data: BackendUser = await response.json();
  return adaptUser(data);
}

/**
 * Update user details
 */
export async function updateUser(
  userId: string,
  user: Partial<AdminUser>,
  token: string
): Promise<AdminUser> {
  const response = await fetch(`${USERS_API_URL}/${userId}`, {
    method: 'PUT',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(toBackendUser(user)),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => null);
    throw new Error(errorData?.detail || 'فشل تحديث بيانات المستخدم.');
  }

  const data: BackendUser = await response.json();
  return adaptUser(data);
}

/**
 * Delete a user
 */
export async function deleteUser(userId: string, token: string): Promise<void> {
  const response = await fetch(`${USERS_API_URL}/${userId}`, {
    method: 'DELETE',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => null);
    throw new Error(errorData?.detail || 'فشل حذف المستخدم.');
  }
}
